import { AdminPage } from "@/components/AdminPage";
import { AdminTable, type Column } from "@/components/AdminTable";
import { OverviewMetricChart } from "@/components/OverviewMetricChart";
import { api } from "../lib/api";
import type { ApiContract } from "@podecontratar/shared/contract/index";
import type { EndpointOutput } from "@podecontratar/shared/contract/endpoint";

type Row = EndpointOutput<ApiContract["GET /admin/entities"]>[number];

function formatDate(iso: string): string {
  return new Date(iso.replace(" ", "T") + "Z").toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function formatDoc(type: "pf" | "pj", doc: string): string {
  if (type === "pf" && doc.length === 11) {
    return `${doc.slice(0, 3)}.${doc.slice(3, 6)}.${doc.slice(6, 9)}-${doc.slice(9)}`;
  }
  if (type === "pj" && doc.length === 14) {
    return `${doc.slice(0, 2)}.${doc.slice(2, 5)}.${doc.slice(5, 8)}/${doc.slice(8, 12)}-${doc.slice(12)}`;
  }
  return doc;
}

const COLUMNS: Column<Row>[] = [
  {
    header: "Empresa",
    width: "26%",
    cell: (r) => (
      <div>
        <p className="truncate font-medium text-text">{r.trade_name ?? r.name}</p>
        <p className="truncate text-[11px] text-text-tertiary">
          CNPJ {formatDoc(r.type, r.document)}
        </p>
      </div>
    ),
  },
  {
    header: "Dono",
    width: "22%",
    hideBelow: "md",
    cell: (r) => (
      <div>
        <p className="truncate text-xs text-text">{r.owner_name ?? "—"}</p>
        <p className="truncate text-[11px] text-text-tertiary">{r.owner_email}</p>
      </div>
    ),
  },
  {
    header: "Papel",
    width: "9%",
    hideBelow: "lg",
    cell: (r) => <span className="text-xs text-text-secondary">{r.role}</span>,
  },
  {
    header: "Slug",
    width: "14%",
    hideBelow: "xl",
    cell: (r) => <code className="truncate text-[11px] text-text-tertiary">{r.slug}</code>,
  },
  {
    header: "Visibilidade",
    width: "10%",
    hideBelow: "md",
    cell: (r) => (
      <span className="text-[11px] text-text-secondary">
        {r.public_profile_visibility}
      </span>
    ),
  },
  {
    header: "Criada em",
    width: "12%",
    hideBelow: "sm",
    cell: (r) => (
      <span className="text-[11px] text-text-secondary">{formatDate(r.created_at)}</span>
    ),
  },
];

export default function AdminEntitiesPage() {
  return (
    <AdminPage
      eyebrow="Empresas"
      title="Entidades (PF + PJ)"
      description="Todas as entidades cadastradas. Inclui PFs (auto-criadas em todo signup) e PJs ativadas via Zarv ID QSA."
    >
      <div className="mb-6">
        <OverviewMetricChart
          metric="entities"
          title="Entidades criadas nos últimos 30 dias"
        />
      </div>
      <AdminTable<Row>
        // `type: "pj"` filters out PF entities — the admin user/PJ split is
        // already at /users, so /entities should never surface PF rows.
        fetchPage={({ q, cursor, limit }) =>
          api.request("GET /admin/entities", { query: { q: q || undefined, type: "pj", limit, cursor } })
        }
        rowKey={(r) => r.id}
        columns={COLUMNS}
        searchPlaceholder="Razão social, CNPJ, slug ou e-mail do dono"
        emptyTitle="Nenhuma empresa cadastrada"
      />
    </AdminPage>
  );
}
